<template>
    <div>
        <h3 v-if="paymentsToDisplay.length > 0">{{ $l10n.__('Liste des paiements') }}</h3>
        <h3 v-else>{{ $l10n.__('Ajouter un nouveau règlement') }}</h3>

        <p class="aright">
            <CompassButton
                v-if="hasReconciledPayment"
                :label="wordingBtnCollapse"
                type="tertiary"
                @click="togglePaymentsDisplay"
            />
        </p>

        <el-table
            v-show="paymentsToDisplay.length > 0"
            :data="paymentsToDisplay"
            :rowKey="rowKey"
            :class="{ 'b-1 br': true, 'br-bottom-0': paymentsToAdd.length > 0 }"
            style="width: 100%"
        >
            <el-table-column :label="$t.__('common.labels.date')" width="100">
                <template #default="scope">
                    {{ $filters.saasDateFormatted(scope.row.date) }}
                </template>
            </el-table-column>
            <el-table-column prop="ref" :label="$t.__('common.labels.reference')">
            </el-table-column>
            <el-table-column :label="$t.__('common.labels.payment_amount')" width="180">
                <template #default="scope">
                    {{ $filters.numberFormatCurrency(scope.row.amount) }}
                </template>
            </el-table-column>
            <el-table-column :label="$t.__('common.labels.payment_status')" width="150">
                <template #default="scope">
                    <div v-if="scope.row.payStepLabel !== undefined">
                        <div :class="'chip chip-' + scope.row.payStepChip"></div>
                        {{ scope.row.payStepLabel }}
                    </div>
                    <div v-else>
                        {{ $l10n.__('Nouveau paiement') }}
                    </div>
                </template>
            </el-table-column>
            <el-table-column :label="$t.__('common.actions.reconcile')" width="140">
                <template #default="scope">
                    <el-checkbox
                        v-model="scope.row.isChecked"
                        :disabled="scope.row.isReconciled"
                        @change="updateStore(scope.row.storeId, scope.row.payid, $event)"
                    >
                        {{ $l10n.__('Rapproché') }}
                    </el-checkbox>
                </template>
            </el-table-column>
        </el-table>

        <!-- Form to add payment -->
        <el-table
            v-show="paymentsToAdd.length > 0"
            :data="paymentsToAdd"
            :rowKey="rowKeyNewPayment"
            :class="{
                'el-table--disable-row-hover br': true,
                'b-1': !paymentsToDisplay.length,
                'bl-1 br-1 bb-1 br-top-0': paymentsToDisplay.length > 0,
            }"
            style="width: 100%"
        >
            <el-table-column :label="$t.__('common.labels.date')" width="100">
                <template #default="scope">
                    {{ $filters.saasDateFormatted(scope.row.date) }}
                </template>
            </el-table-column>
            <el-table-column :label="$t.__('common.labels.reference')">
                <template #default="scope">
                    <el-input
                        v-model="scope.row.ref"
                        :placeholder="$t.__('common.labels.a_reference')"
                        :disabled="scope.row.isLocked"
                    ></el-input>
                </template>
            </el-table-column>
            <el-table-column :label="$t.__('common.labels.payment_amount')" width="180">
                <template #default="scope">
                    <input-number
                        v-if="isSaleDoc"
                        v-model="scope.row.amount"
                        :precision="numberFormat.precision"
                        :separatorDecimal="numberFormat.decimal"
                        :separatorThousand="numberFormat.thousand"
                        :disabled="scope.row.isLocked"
                        :controls="false"
                        :placeholder="$t.__('common.labels.amount')"
                        size="small"
                        :min="0.01"
                    >
                    </input-number>

                    <input-number
                        v-else
                        v-model="scope.row.amount"
                        :precision="numberFormat.precision"
                        :separatorDecimal="numberFormat.decimal"
                        :separatorThousand="numberFormat.thousand"
                        :disabled="scope.row.isLocked"
                        :controls="false"
                        :placeholder="$t.__('common.labels.amount')"
                        size="small"
                        :min="0.01"
                    >
                    </input-number>
                </template>
            </el-table-column>
            <el-table-column :label="$t.__('common.labels.type')" width="150">
                <template #default="scope">
                    <el-select v-model="scope.row.pay_type">
                        <el-option
                            v-for="type in payTypes"
                            :key="type.id"
                            :label="type.value"
                            :value="type.id"
                        >
                        </el-option>
                    </el-select>
                </template>
            </el-table-column>
            <el-table-column :label="$t.__('common.actions.reconcile')" width="140">
                <template #default="scope">
                    <i
                        class="icon-trash link"
                        style="float: right"
                        @click="removeSelectedLine(scope.row.id)"
                    ></i>
                    <rb-new-payment-checkbox
                        :default="false"
                        :document="document"
                        :reference="scope.row.ref"
                        :pay_type="scope.row.pay_type"
                        :amount="scope.row.amount"
                        @new-payment-confirmed="removeSelectedLine(scope.row.id)"
                    />
                </template>
            </el-table-column>
        </el-table>

        <p class="aright">
            <CompassButton
                v-if="canAddPayment"
                :label="$t.__('common.actions.add_and_reconcile__new_payment')"
                type="tertiary"
                @click="addNewPayment"
            />
        </p>
    </div>
</template>

<script>
import Mixin from './../../mixins/interlineReconciliation.js';
import NewPaymentCheckbox from './NewPaymentCheckbox.vue';
import InputNumber from 'vcomponents/utilities/InputNumber.vue';

export default {
    components: {
        'input-number': InputNumber,
        'rb-new-payment-checkbox': NewPaymentCheckbox,
    },
    mixins: [Mixin],
    props: ['document', 'payments'],
    watch: {
        paymentsToDisplay(newArray, oldArray) {
            if (newArray.length == 0 && this.paymentsToAdd.length == 0) {
                this.addNewPayment();
            }
        },
    },
    methods: {
        updateStore(storeid, paymentid, check) {
            if (check) {
                const selectedPayment = this.payments.find((payment) => {
                    return payment.payid == paymentid;
                });

                const reconciliation = {
                    id: '_' + Math.random().toString(36).substr(2, 9),
                    amount: selectedPayment.amount,
                    document: this.document,
                    paymentid: paymentid,
                    pay_type: selectedPayment.amountType,
                };
                this.addReconciled(reconciliation);
            } else {
                const currentPayment = this.reconciled.find((payment) => {
                    return payment.id == storeid;
                });
                this.delReconciled(storeid);

                if (paymentid === undefined) {
                    this.paymentsToAdd.unshift(currentPayment);
                }
            }
        },
        addNewPayment() {
            const payment = {
                id: '_' + Math.random().toString(36).substr(2, 9),
                ref: '',
                date: this.transactionDate,
                amount: this.defaultNewPayment,
                isLocked: false,
                pay_type: this.getDefaultPayType(),
            };
            this.paymentsToAdd.push(payment);
        },
    },
};
</script>
